import React, { useMemo, useState } from 'react';
import { ArrowDownOutlined, ArrowUpOutlined, EyeInvisibleFilled, EyeOutlined } from '@ant-design/icons';
import { Avatar, Card, Skeleton, theme, Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { useTheme } from '../../contexts/ui/ThemeContext';
import { EThemeNames } from '../../../domain/types/themePresets';

const { useToken } = theme;

interface StatsTileProps {
  isLoading: boolean;
  isConfidential: boolean;
  item: {
    icon: React.ReactNode;
    title: string;
    value: number | string | React.ReactNode;
    pagePath?: string;
    onClick?: (value: number | string | React.ReactNode) => void;
    info?: string;
    color?: string;
    /** Écart contre la période précédente, en fraction (0.12 = +12 %).
     *  `inverted` pour les métriques où une hausse est mauvaise (taux d'échec). */
    delta?: { value: number; inverted?: boolean };
    /**
     * Contre quoi `delta` (ou l'écart écrit dans `info`) se lit, en clair :
     * « Comparé à samedi 5 septembre ». Rendu en infobulle plutôt qu'en toutes
     * lettres sous le chiffre — la tuile n'a pas la place, et la question ne se
     * pose qu'une fois. Sans cette propriété, un « -64,3 % » ne dit pas s'il
     * compare à la veille ou au même jour de la semaine passée.
     */
    comparison?: string;
    /** Série pour la sparkline. Moins de 2 points : rien n'est rendu. */
    sparkline?: number[];
    /**
     * Décomposition de `value` en parts, rendue en une barre empilée sous le
     * chiffre. Remplace `info` quand la répartition se compare mieux qu'elle ne
     * se lit : deux longueurs côte à côte disent tout de suite laquelle domine.
     * `label` est affiché tel quel, effectif compris — le formatage des nombres
     * reste chez l'appelant.
     */
    split?: { label: string; value: number; color: string }[];
  };
}

const StatsTile: React.FC<StatsTileProps> = ({
  isLoading,
  isConfidential,
  item,
}) => {
  const navigate = useNavigate();
  const [showConfidentialData, setShowConfidentialData] = useState(false);
  const { token } = useToken();
  const { themeName } = useTheme();
  const isDark = themeName === EThemeNames.LEEDFLOW_DARK;

  const isGoodDelta = item.delta
    ? item.delta.inverted
      ? item.delta.value < 0
      : item.delta.value > 0
    : false;
  const deltaColor = item.delta
    ? item.delta.value === 0
      ? token.colorTextSecondary
      : isGoodDelta
        ? token.colorSuccess
        : token.colorError
    : undefined;

  const showSparkline = (item.sparkline?.length ?? 0) >= 2;
  // Un total nul rendrait des largeurs NaN : la barre n'est alors pas rendue.
  const splitTotal = item.split?.reduce((sum, part) => sum + part.value, 0) ?? 0;

  const deltaFormatter = useMemo(
    () =>
      new Intl.NumberFormat('fr-FR', {
        style: 'percent',
        signDisplay: 'exceptZero',
        maximumFractionDigits: 1,
      }),
    []
  );

  const sparklineOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        type: 'area',
        sparkline: { enabled: true },
        background: 'transparent',
      },
      stroke: {
        curve: 'smooth',
        width: 2,
        colors: [deltaColor ?? token.colorPrimary],
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: isDark ? 0.4 : 0.3,
          opacityTo: 0,
        },
        colors: [deltaColor ?? token.colorPrimary],
      },
      tooltip: { enabled: false },
    }),
    [deltaColor, isDark, token.colorPrimary]
  );

  return (
    <Card
      style={{ width: '100%', padding: 0 }}
      hoverable
      size="small"
      styles={{
        body: {
          padding: 16,
          height: '100%',
        },
      }}
      onClick={function () {
        if (isLoading) return;

        if (item.onClick) {
          item.onClick(item.value);
          return;
        }

        if (item.pagePath) {
          navigate(item.pagePath);
        }
      }}
    >
      <div className="overview-page-stats__card-content">
        {isLoading ? (
          <Skeleton active paragraph />
        ) : (
          <>
            <Avatar
              className="overview-page-stats__icon"
              style={{ backgroundColor: '#f9f7ff', fontSize: 26, color: item.color }}
              size={64}
            >
              {item.icon}
            </Avatar>
            <div style={{ marginLeft: '1rem', height: '100%' }}>
              <p style={{ color: '#949494', marginBottom: '0.25rem' }}>
                {item.title}
              </p>
              {isConfidential && !showConfidentialData ? (
                <span className="overview-page-stats__count blur">
                  {item.value}
                </span>
              ) : (
                <span
                  style={{ color: item.color }}
                  className="overview-page-stats__count"
                >
                  {item.value}
                </span>
              )}
              {item.info && (
                // `title` absent : antd n'affiche alors aucune infobulle, donc
                // l'enrobage est sans effet sur les tuiles sans comparaison.
                <Tooltip title={item.comparison}>
                  <span
                    className={`overview-page-stats__info ${
                      isConfidential && !showConfidentialData ? 'blur' : ''
                    }`}
                  >
                    {item.info}
                  </span>
                </Tooltip>
              )}
              {splitTotal > 0 && (
                <div
                  className={isConfidential && !showConfidentialData ? 'blur' : ''}
                  style={{ marginTop: 8 }}
                >
                  <div
                    style={{
                      display: 'flex',
                      height: 8,
                      borderRadius: 999,
                      overflow: 'hidden',
                      background: token.colorFillSecondary,
                    }}
                  >
                    {item.split?.map((part) => (
                      <div
                        key={part.label}
                        style={{
                          width: `${(part.value / splitTotal) * 100}%`,
                          background: part.color,
                        }}
                      />
                    ))}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      gap: 12,
                      marginTop: 6,
                      flexWrap: 'wrap',
                      fontSize: 12,
                      color: '#949494',
                    }}
                  >
                    {item.split?.map((part) => (
                      <span
                        key={part.label}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
                      >
                        <span
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            background: part.color,
                            flexShrink: 0,
                          }}
                        />
                        {part.label}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {item.delta && (
                <Tooltip title={item.comparison}>
                  <div
                    className={
                      isConfidential && !showConfidentialData ? 'blur' : ''
                    }
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      marginTop: 2,
                      fontSize: 13,
                      color: deltaColor,
                    }}
                  >
                    {item.delta.value !== 0 &&
                      (item.delta.value > 0 ? (
                        <ArrowUpOutlined />
                      ) : (
                        <ArrowDownOutlined />
                      ))}
                    <span>{deltaFormatter.format(item.delta.value)}</span>
                  </div>
                </Tooltip>
              )}
              {showSparkline && (
                <div
                  className={
                    isConfidential && !showConfidentialData ? 'blur' : ''
                  }
                  style={{ width: 96, marginTop: 6 }}
                >
                  <ReactApexChart
                    options={sparklineOptions}
                    series={[{ data: item.sparkline ?? [] }]}
                    type="area"
                    height={40}
                  />
                </div>
              )}
            </div>
            <div
              style={{ position: 'absolute', right: 0, marginRight: '1rem' }}
            >
              {isConfidential &&
                (showConfidentialData ? (
                  <Tooltip title="Masquer">
                    <EyeInvisibleFilled
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowConfidentialData(!showConfidentialData);
                      }}
                      style={{ fontSize: '18px', padding: '8px' }}
                    />
                  </Tooltip>
                ) : (
                  <Tooltip title="Afficher">
                    <EyeOutlined
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowConfidentialData(!showConfidentialData);
                      }}
                      style={{ fontSize: '18px', padding: '8px' }}
                    />
                  </Tooltip>
                ))}
            </div>
          </>
        )}
      </div>
    </Card>
  );
};

export default StatsTile;
